// The week's state in one line: draft, approved vN by whom and why, or no plan;
// and, under it, a proposal waiting with what it improves.
import type { Forecast, VersionSummary } from "../lib/types";
import { stamp } from "../lib/days";

export interface StatusBannerProps {
  shown: VersionSummary | null;
  /** The newest proposal still waiting, if any. */
  proposal?: VersionSummary | null;
  /** The approved version, to compare the proposal against. */
  approved?: VersionSummary | null;
  night?: boolean;
  onCompare?: () => void;
}

const devices = (f: Forecast | null | undefined) => (f ? f.week : null);

/** "46 devices instead of 0" and the rest of what a proposal changes. */
export function improvement(proposal: VersionSummary, against: VersionSummary | null | undefined): string {
  const bits: string[] = [];
  const now = devices(proposal.forecast), was = devices(against?.forecast);
  if (now != null && was != null && now !== was) bits.push(`${now} devices instead of ${was}`);
  else if (now != null) bits.push(`${now} devices`);
  if (proposal.changedCells != null) bits.push(`${proposal.changedCells} half-days changed`);
  return bits.join(", ");
}

export function StatusBanner({ shown, proposal, approved, night, onCompare }: StatusBannerProps) {
  let word: string, kind: string, text: string;
  if (!shown) { word = "No plan"; kind = "none"; text = "No plan for this week yet"; }
  else if (shown.kind === "approved") {
    word = `Approved v${shown.version}`; kind = "approved";
    text = `Approved by ${shown.authorName ?? "someone"}${shown.approvedAt ? `, ${stamp(shown.approvedAt)}` : ""}`;
  } else if (shown.kind === "proposal") {
    word = `Proposal v${shown.version}`; kind = "proposal"; text = `Proposed by ${shown.authorName ?? "the planner"}, ${stamp(shown.createdAt)}: not approved`;
  } else if (shown.kind === "draft") {
    word = `Draft v${shown.version}`; kind = "draft"; text = `Draft by ${shown.authorName ?? "someone"}, ${stamp(shown.createdAt)}: not approved yet`;
  } else {
    word = `v${shown.version} ${shown.kind}`; kind = "draft"; text = `${shown.kind === "superseded" ? "Replaced" : "Rejected"}: an old version`;
  }
  const waiting = proposal && proposal.kind === "proposal" && proposal.id !== shown?.id ? proposal : null;
  return (
    <section className={`status${night ? " status--night" : ""}`} aria-label="Plan status" data-testid="status-banner">
      <div className="status__row">
        <span className={`status__word status__word--${kind}`}>{word}</span>
        <span className="status__text">{text}</span>
        {shown?.reason ? <span className="status__why">“{shown.reason}”</span> : null}
      </div>
      {waiting && !night ? (
        <div className="status__proposal">
          <span className="status__word status__word--proposal">Proposal v{waiting.version} waiting</span>
          <span>{improvement(waiting, approved ?? shown)}</span>
          {onCompare ? <button type="button" className="btn btn--sm" onClick={onCompare}>Compare</button> : null}
        </div>
      ) : null}
    </section>
  );
}
